import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import SeoMeta, { SITE_URL, makeDescription } from "../components/common/SeoMeta";
import { fetchPublicContent } from "../data/publicContent";
import "./styles/blog-public.css";

function formatDate(iso) {
    if (!iso) return "";
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-ZA", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
}

function estimateReadMinutes(body = "") {
    const words = body.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
}

const BlogShell = ({ children }) => (
    <div className="page-content">
        <NavBar active="blog" />
        <div className="content-wrapper">
            {children}
            <div className="page-footer"><Footer /></div>
        </div>
    </div>
);

const ReadBlog = () => {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchPublicContent("blogs", { id })
            .then((item) => {
                if (cancelled) return;
                setNotFound(false);
                setPost(item);
            })
            .catch(() => !cancelled && setNotFound(true))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [id]);

    if (loading) {
        return (
            <BlogShell>
                <div className="pb-root"><div className="pb-state">Loading…</div></div>
            </BlogShell>
        );
    }

    if (notFound || !post) {
        return (
            <BlogShell>
                <div className="pb-root">
                    <div className="pb-state">
                        This post couldn't be found.
                        <div style={{ marginTop: 12 }}>
                            <Link to="/blog" className="pb-back-link">← Back to Blog</Link>
                        </div>
                    </div>
                </div>
            </BlogShell>
        );
    }

    // Blank line between lines = new paragraph, matching the admin's convention.
    const paragraphs = (post.body || "").split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    const description = makeDescription(post.description, post.body);
    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description,
        url: `${SITE_URL}/blog/${encodeURIComponent(post.id)}`,
        mainEntityOfPage: `${SITE_URL}/blog/${encodeURIComponent(post.id)}`,
        author: { "@type": "Person", name: "Jabu Zwane", url: SITE_URL },
        publisher: {
            "@type": "Organization",
            name: "Jabu Zwane",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/lg.png` },
        },
        ...(post.date ? { datePublished: post.date } : {}),
        ...(post.coverUrl ? { image: post.coverUrl } : {}),
        ...(post.keywords?.length ? { keywords: post.keywords.join(", ") } : {}),
    };

    return (
        <BlogShell>
            <SeoMeta
                title={`${post.title} | Jabu Zwane`}
                description={description}
                path={`/blog/${encodeURIComponent(post.id)}`}
                image={post.coverUrl}
                type="article"
                keywords={post.keywords}
                structuredData={articleSchema}
            />
            <div className="pb-root pb-root--article">
                <Link to="/blog" className="pb-back-link">← Back to Blog</Link>

                <div className="pb-article-header">
                    {post.keywords?.length > 0 && (
                        <div className="pb-article-tags">
                            {post.keywords.map(k => <span key={k} className="pb-card__tag">{k}</span>)}
                        </div>
                    )}
                    <h1 className="pb-article-title">{post.title}</h1>
                    <div className="pb-article-meta">
                        <span>{formatDate(post.date)}</span>
                        <span className="pb-dot">·</span>
                        <span>{estimateReadMinutes(post.body)} min read</span>
                    </div>
                </div>

                {post.coverUrl && (
                    <div className="pb-article-cover" style={{ backgroundImage: `url(${post.coverUrl})` }} />
                )}

                <div className="pb-article-body">
                    {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
            </div>
        </BlogShell>
    );
};

export default ReadBlog;